import { FiArrowRight } from 'react-icons/fi';
import styled from 'styled-components';

/* Adaptado de um componente de terceiros (flow-button do 21st.dev): a
   animação — seta entrando por um lado, saindo pelo outro, e um círculo que
   se expande do centro até cobrir o botão — foi mantida. As cores mudaram
   duas vezes: primeiro do preto/branco original para os tokens do SGRLock, e
   depois invertidas por pedido — em repouso o botão precisava chamar mais
   atenção (era uma borda quase invisível sobre o card translúcido), então
   agora é preenchido em --primary (âmbar) com texto escuro, igual o Button
   comum; no hover ele inverte para um círculo escuro que cresce do centro,
   com texto branco por cima. */

const IconBase = styled(FiArrowRight)`
  position: absolute;
  /* Sem isso as setas (position: absolute, sem z-index próprio) empilham na
     ordem do DOM — o círculo (que vem depois no markup) pintava por cima da
     seta esquerda assim que crescia, deixando-a "invisível" durante a
     animação de entrada. Precisa ficar acima do círculo o tempo todo. */
  z-index: 2;
  width: 16px;
  height: 16px;
  color: var(--primary-foreground);
  transition: left 0.8s cubic-bezier(0.34, 1.56, 0.64, 1),
    right 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.6s ease;
`;

export const LeftArrow = styled(IconBase)`
  left: -25%;
`;

export const RightArrow = styled(IconBase)`
  right: 1rem;
`;

export const Circle = styled.span`
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: var(--foreground);
  border-radius: 50%;
  opacity: 0;
  transition: width 0.8s cubic-bezier(0.19, 1, 0.22, 1),
    height 0.8s cubic-bezier(0.19, 1, 0.22, 1),
    opacity 0.8s cubic-bezier(0.19, 1, 0.22, 1);
`;

export const Label = styled.span`
  position: relative;
  z-index: 2;
  transform: translateX(-0.75rem);
  color: var(--primary-foreground);
  transition: transform 0.8s ease-out, color 0.6s ease;
`;

export const Wrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
  width: fit-content;

  /* O fundo fica sempre âmbar — quem "escurece" o botão no hover é só o
     Circle crescendo por cima (mesmo mecanismo do componente original); a
     Wrapper nunca troca a própria cor de fundo, senão as duas animações
     brigam entre si. */
  border: 1.5px solid var(--primary);
  /* Fixo em 24px, não var(--radius-full) (9999px) — o botão tem ~44px de
     altura (padding + linha do texto), então 24px já renderiza como pílula
     completa (o navegador limita o raio a metade da altura), visualmente
     idêntico a 9999px em repouso. A diferença aparece só na transição: saindo
     de 9999px, o valor numérico passa quase o tempo todo acima da metade da
     altura — a borda fica "presa" no visual arredondado e só destrava no
     fim, dando a impressão de que a transição começa atrasada. Partindo de um
     valor já próximo do alvo (12px), o arredondamento começa a mudar assim
     que o hover começa, junto com o resto da animação. */
  border-radius: 24px;
  background: var(--primary);
  padding: 0.75rem 2rem;

  font-family: var(--font-sans);
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  /* Mesma duração das outras animações (círculo, setas, texto) — em 0.6s a
     borda chegava ao estado final antes do resto e quebrava a sensação de
     tudo se mover junto. */
  transition: border-color 0.8s cubic-bezier(0.23, 1, 0.32, 1),
    border-radius 0.8s cubic-bezier(0.23, 1, 0.32, 1);

  &:active {
    transform: scale(0.95);
  }

  /* Sem isso o botão continua parecendo clicável durante uma ação em curso —
     e o :hover ainda dispararia a animação inteira. */
  &:disabled {
    cursor: default;
    opacity: 0.55;
    transform: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(255, 188, 3, 0.4);
  }

  &:hover:not(:disabled) {
    border-color: var(--foreground);
    border-radius: 12px;
  }

  &:hover:not(:disabled) ${Label} {
    color: #fff;
    transform: translateX(0.75rem);
  }

  &:hover:not(:disabled) ${Circle} {
    width: 220px;
    height: 220px;
    opacity: 1;
  }

  &:hover:not(:disabled) ${LeftArrow} {
    left: 1rem;
    color: #fff;
  }

  &:hover:not(:disabled) ${RightArrow} {
    right: -25%;
    color: #fff;
  }
`;
